import { z } from "zod";

// ── Self-update (device → backend) ──────────────────────────────────

export const selfUpdateRequestSchema = z.object({
  device_serial: z.string().min(1).max(64),
  current_version: z.string().min(1).max(64),
  bundle_id: z.string().min(1).max(255),
});

export const selfUpdateResponseSchema = z.object({
  status: z.literal("accepted"),
});

// ── Admin: Jobs ─────────────────────────────────────────────────────

export const createJobRequestSchema = z.object({
  app_id: z.number().int().positive(),
  target_version: z.string().min(1).max(64),
});

export const retryJobRequestSchema = z.object({
  statuses: z.array(z.enum(["trigger_failed", "timeout", "management_failed"])).min(1),
});

// ── Admin: Latest versions ──────────────────────────────────────────

export const updateLatestVersionSchema = z.object({
  version: z.string().min(1).max(64),
});

// ── Device update record statuses ───────────────────────────────────

export const deviceStatusSchema = z.enum([
  "pending",
  "up_to_date",
  "not_installed",
  "triggering",
  "triggered",
  "trigger_failed",
  "confirmed",
  "timeout",
  "unenrolled",
  // Devices already at the target version when the job was created. They are
  // never enqueued/triggered — recorded only so the admin can see them in the
  // device table. Terminal and NOT retryable.
  "skipped",
  // Unmanaged app (SimpleMDM 403 "installed app is not managed"): the worker has
  // asked SimpleMDM to take management (request_management). The sweeper polls
  // until the device reports the app managed, then re-enqueues the update.
  // Non-terminal.
  "requesting_management",
  // Management takeover did not complete within the window (device offline, or
  // SimpleMDM refused). Terminal but admin-retryable (retry re-attempts takeover).
  "management_failed",
  // Admin-initiated delete + reinstall in flight (sweeper-driven, by-bundleId).
  // Sub-phase tracked in reinstall_phase. Non-terminal; always times out.
  "reinstalling",
  // Delete/reinstall didn't reach a managed install within the window. Terminal.
  // Re-runnable via the per-row Reinstall action (not the bulk retry).
  "reinstall_failed",
]);

/**
 * Sub-phase of the `reinstalling` status (sweeper-driven state machine).
 * awaiting_uninstall → (best-effort DELETE) → awaiting_removal →
 * (app gone → push_apps) → awaiting_install → (managed → confirmed).
 */
export const reinstallPhaseSchema = z.enum([
  "awaiting_uninstall",
  "awaiting_removal",
  "awaiting_install",
]);

export const TERMINAL_STATUSES = [
  "confirmed",
  "up_to_date",
  "not_installed",
  "trigger_failed",
  "timeout",
  "unenrolled",
  "skipped",
  "management_failed",
  "reinstall_failed",
] as const;

export const RETRYABLE_STATUSES = ["trigger_failed", "timeout", "management_failed"] as const;

/**
 * Status display priority — most operationally urgent first. Drives the default
 * sort of the device table AND the order of the status filter dropdown, so both
 * the backend ORDER BY and the frontend share one definition.
 */
export const STATUS_IMPORTANCE = [
  "timeout",
  "trigger_failed",
  "management_failed",
  "reinstall_failed",
  "requesting_management",
  "reinstalling",
  "confirmed",
  "triggered",
  "triggering",
  "pending",
  "up_to_date",
  "skipped",
  "not_installed",
  "unenrolled",
] as const;

/** Per-status counts for a job's device records (one entry per DeviceStatus). */
export type DeviceStatusCounts = Record<z.infer<typeof deviceStatusSchema>, number>;

/**
 * Collapse per-status counts into the display buckets used by the progress
 * column and the detail cards. `skippedDevices`/`totalDevices` come from the
 * job row (not the per-status counts) so the math is correct for legacy jobs
 * created before skipped devices were persisted as rows.
 */
export function bucketCounts(
  counts: DeviceStatusCounts,
  skippedDevices: number,
  totalDevices: number,
): {
  succeeded: number;
  failed: number;
  timeout: number;
  skipped: number;
  inProgress: number;
  total: number;
} {
  const succeeded = counts.confirmed + counts.up_to_date;
  // `failed` is hard failures only. `timeout` is surfaced separately — a device
  // that was triggered but hasn't confirmed within the window. It is NOT a hard
  // failure (the sweeper can still late-confirm it as inventory catches up) and
  // the UI tracks it distinctly (own badge + a separate "Retry Timeouts" action).
  // `requesting_management` is in-flight self-healing → folds into inProgress.
  const failed =
    counts.trigger_failed +
    counts.not_installed +
    counts.unenrolled +
    counts.management_failed +
    counts.reinstall_failed;
  const timeout = counts.timeout;
  const skipped = skippedDevices;
  const inProgress = Math.max(0, totalDevices - succeeded - failed - timeout - skipped);
  return { succeeded, failed, timeout, skipped, inProgress, total: totalDevices };
}

// ── Job statuses ────────────────────────────────────────────────────

export const jobStatusSchema = z.enum(["running", "completed"]);

// ── Query params ────────────────────────────────────────────────────

export const paginationSchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(200).default(50),
});

/** Columns the device table can be sorted by (server-side). */
export const deviceSortFieldSchema = z.enum([
  "status",
  "deviceName",
  "serial",
  "versionAtStart",
  "currentVersion",
]);

export const deviceRecordFilterSchema = paginationSchema.extend({
  status: deviceStatusSchema.optional(),
  search: z.string().optional(),
  sortBy: deviceSortFieldSchema.default("status"),
  sortDir: z.enum(["asc", "desc"]).default("asc"),
});
